import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../../app/store";
import { updateSendCommand } from "../../../redux/monitoring/monitoringSlice";
import notifyCommandBlocked from "../../../utils/commandFeedback";

/**
 * Reports commands that no monitoring socket picked up.
 *
 * WHY A WATCHDOG RATHER THAN A GUARD IN THE SOCKET
 *
 * MonitoringGrid renders one MonitoringWebSocket per server, each holding the
 * `layouts` for that server alone, and each ignores commands addressed
 * elsewhere:
 *
 *   shouldHandleCommand = sendCommand && targetBankId
 *                         && (serverId matches || command has no serverId)
 *                         && bank is in THIS socket's layouts
 *
 * That filter is correct and must stay — with N servers mounted, N-1 instances
 * are supposed to skip any given command. Notifying from inside the socket
 * would therefore fire N-1 false alarms for every successful command.
 *
 * What has no owner is the case where NO instance matches: the bank belongs to
 * a server that is not mounted, or `layouts` has not hydrated yet. Every
 * instance skips, nobody sends, nobody reports, and redux keeps
 * `sendCommand: true` forever — so the button's in-flight spinner never clears
 * either. This hook is that owner, mounted exactly once alongside the sockets.
 *
 * HOW IT DECIDES
 *
 * Both the send and the failure paths in MonitoringWebSocket end by dispatching
 * `updateSendCommand({ sendCommand: false })`. So "still true after a grace
 * period" means unclaimed. The check is a timer rather than a synchronous test
 * because socket selection happens in a sibling component's effect, which runs
 * after this one in the same commit.
 *
 * The timer is cancelled whenever the flag clears, so a claimed command never
 * reaches the warning — that is what keeps this from crying wolf on the happy
 * path.
 */

/** Grace period before an unhandled command is declared undeliverable. */
export const UNCLAIMED_COMMAND_TIMEOUT_MS = 2000;

export default function useUnclaimedCommandWatchdog(): void {
  const dispatch = useDispatch();
  const { sendCommand } = useSelector((state: RootState) => state.monitoring);
  const { theme } = useSelector((state: RootState) => state.home);

  // Read through a ref inside the timer so the callback always sees the latest
  // theme without the timer restarting every time the theme changes.
  const themeRef = useRef(theme);
  themeRef.current = theme;

  const isPending = Boolean(sendCommand?.sendCommand);
  const bankId = sendCommand?.body?.bankId;

  useEffect(() => {
    if (!isPending) return undefined;

    const timer = setTimeout(() => {
      notifyCommandBlocked(
        "Command not delivered",
        "No connected monitoring server accepted this command, so it never reached the controller. " +
          "Check that the bank's edge server is online and shown as connected, then try again.",
        themeRef.current,
      );
      // Clear the flag as well as reporting it. Leaving it set would pin every
      // action button's spinner on and suppress the next command's watchdog,
      // because the effect would never see a false→true transition again.
      dispatch(updateSendCommand({ sendCommand: false, serverId: "", body: {} }));
    }, UNCLAIMED_COMMAND_TIMEOUT_MS);

    return () => clearTimeout(timer);
    // `bankId` participates so that two commands in a row each get their own
    // grace period instead of sharing the first one's timer.
  }, [isPending, bankId, dispatch]);
}
